import {
  Home,
  MessageSquare,
  Layers,
  Trophy,
  BookOpen,
  FileText,
  Calculator,
  ClipboardList,
  Pill,
  Stethoscope,
  Rss,
  Users,
  Zap,
  Target,
  BarChart3,
  Settings,
  GraduationCap,
  Swords,
  Newspaper,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

export function AppSidebar() {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const mainItems = [
    { title: t('common.sidebar.dashboard'), url: "/dashboard", icon: Home },
    { title: t('common.sidebar.chatAI'), url: "/chat", icon: MessageSquare },
    { title: t('common.sidebar.flashcards'), url: "/flashcards", icon: Layers },
    { title: t('common.sidebar.revisionSheets'), url: "/revision-sheets", icon: FileText },
    { title: t('common.sidebar.dailyQuiz'), url: "/daily-quiz", icon: Target },
  ];

  const learningItems = [
    { title: t('common.sidebar.study'), url: "/study", icon: BookOpen },
    { title: t('common.sidebar.courses'), url: "/cours", icon: GraduationCap },
    { title: t('common.sidebar.clinicalCases'), url: "/urgentiste-masque", icon: Swords },
    { title: t('common.sidebar.challenges'), url: "/challenges", icon: Zap },
    { title: t('common.sidebar.revisionExpress'), url: "/revision-express", icon: Zap },
  ];

  const toolItems = [
    { title: t('common.sidebar.calculators'), url: "/calculator", icon: Calculator },
    { title: t('common.sidebar.checklists'), url: "/checklists", icon: ClipboardList },
    { title: t('common.sidebar.medications'), url: "/medications", icon: Pill },
    { title: t('common.sidebar.coding'), url: "/coding", icon: Stethoscope },
    { title: t('common.sidebar.guidelines'), url: "/guidelines", icon: Rss },
  ];

  const socialItems = [
    { title: t('common.sidebar.forum'), url: "/community", icon: Users },
    { title: t('common.sidebar.articles', 'Articles'), url: "/articles", icon: Newspaper },
    { title: t('common.sidebar.scores'), url: "/scores", icon: BarChart3 },
    { title: t('common.sidebar.achievements'), url: "/achievements", icon: Trophy },
    { title: t('common.sidebar.hallOfFame'), url: "/hall-of-fame", icon: Trophy },
  ];

  const renderGroup = (label: string, items: typeof mainItems) => (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton asChild>
                <NavLink
                  to={item.url}
                  end
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-muted-foreground hover:bg-primary/5 hover:text-foreground transition-colors"
                  activeClassName="bg-primary/10 text-primary font-medium"
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.title}</span>}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarContent className="pt-16">
        {renderGroup(t('common.sidebar.groupMain'), mainItems)}
        {renderGroup(t('common.sidebar.groupLearning'), learningItems)}
        {renderGroup(t('common.sidebar.groupTools'), toolItems)}
        {renderGroup(t('common.sidebar.groupCommunity'), socialItems)}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/settings"
                    end
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-muted-foreground hover:bg-primary/5 hover:text-foreground transition-colors"
                    activeClassName="bg-primary/10 text-primary font-medium"
                  >
                    <Settings className="h-4 w-4 shrink-0" />
                    {!collapsed && <span>{t('common.sidebar.settings')}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}